import { useFormik } from 'formik'
import React, { useEffect } from 'react'
import { NumericFormat } from 'react-number-format'
import ModalComponent from '..'
import {
  getCurrencySymbol,
  parseCurrencyToNumber,
  removeElementAtIndex,
} from '../../../helpers'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useCreateDiscountMutation,
  useEditDiscountMutation,
  useLazyGetSingleDiscountsQuery,
} from '../../../redux-toolkits/voucher/voucher.slice'
import {
  CreateDiscount,
  DiscountType,
  StatusType,
  ValueType,
} from '../../../redux-toolkits/discounts/discounts.type'
import { Product } from '../../../redux-toolkits/products/products.type'
import { AddDiscountSchema } from '../../../validation/validate'
import { Button } from '../../button'
import Icon from '../../icon'
import Dropdown from '../../inputs/dropdown'
import { TextInput } from '../../inputs/textInput'
import Spinner from '../../spinner/Spinner'
import CustSwitch from '../../switch'
import { TableComponent } from '../../table'
import { SelectProductModal } from './selectProductModal'

const types: DiscountType[] = ['items', 'delivery', 'cart']
const valueType: ValueType[] = ['percent', 'constant']
const statusType: StatusType[] = ['inactive', 'active']

export const AddDiscountModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  edit?: boolean
  discountId?: string
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  edit = false,
  discountId,
}) => {
  const [createDiscount, { isLoading: creating }] = useCreateDiscountMutation()
  const [editDiscount, { isLoading: editing }] = useEditDiscountMutation()

  const [fetchDiscount, { isFetching: fetchingSingleDiscount }] =
    useLazyGetSingleDiscountsQuery()

  const { modal, modalOpen, handleModalOpen, setModalOpen, handleModalClose } =
    useModalControl()

  const {
    values,
    errors,
    handleBlur,
    setFieldValue,
    handleChange,
    handleSubmit,
    touched,
    resetForm,
  } = useFormik({
    initialValues: {
      minimumAmount: '',
      maximumAmount: '',
      type: '',
      items: [] as Product[],
      valueType: '',
      code: '',
      value: '',
      usageLimit: '',
      userUsageLimit: '',
      status: '',
      autoApply: false,
    },
    enableReinitialize: true,
    validationSchema: AddDiscountSchema,
    onSubmit: (vals) => {
      const type: DiscountType = vals.type as DiscountType
      const valueType: ValueType = vals.valueType as ValueType
      const status: StatusType = vals.status as StatusType
      const payload: Partial<CreateDiscount> = {
        minimumAmount: parseCurrencyToNumber(vals.minimumAmount),
        maximumAmount: parseCurrencyToNumber(vals.maximumAmount),
        value: vals.value,
        code: vals.code,
        items:
          type === 'items' && Array.isArray(vals.items)
            ? vals.items.map((product_) => {
                const product = product_ as unknown as Product
                return product._id
              })
            : undefined,
        valueType,
        usageLimit: Number(vals.usageLimit),
        userUsageLimit: Number(vals.userUsageLimit),
        type,
        status,
        autoApply: vals.autoApply,
      }

      edit
        ? editDiscount({
            id: discountId ?? '',
            data: payload as CreateDiscount,
          })?.then((resp) => {
            if (resp.data?.success) {
              resetForm()
              onSuccessCallback()
            }
          })
        : createDiscount(payload as CreateDiscount)?.then((resp) => {
            if (resp.data?.success) {
              resetForm()
              onSuccessCallback()
            }
          })
    },
  })

  useEffect(() => {
    if (open && edit && discountId) {
      fetchDiscount({ id: discountId }).then((resp) => {
        if (resp.data?.success) {
          setFieldValue(
            'minimumAmount',
            String(resp.data.data.discount.minimumAmount),
          )
          setFieldValue(
            'maximumAmount',
            String(resp.data.data.discount.maximumAmount),
          )
          setFieldValue('type', resp.data.data.discount.type)
          setFieldValue('items', resp.data.data.discount.items)
          setFieldValue('valueType', resp.data.data.discount.valueType)
          setFieldValue('code', resp.data.data.discount.code)
          setFieldValue(
            'value',
            resp.data.data.discount.value
              ? String(resp.data.data.discount.value)
              : undefined,
          )
          setFieldValue(
            'usageLimit',
            resp.data.data.discount.usageLimit
              ? String(resp.data.data.discount.usageLimit)
              : undefined,
          )
          setFieldValue(
            'userUsageLimit',
            resp.data.data.discount.userUsageLimit
              ? String(resp.data.data.discount.userUsageLimit)
              : undefined,
          )
          setFieldValue('status', String(resp.data.data.discount.status))
          setFieldValue(
            'autoApply',
            resp?.data?.data?.discount?.autoApply ?? false,
          )
        }
      })
    }
  }, [edit, discountId, open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        setFieldValue('items', [])
        resetForm()
        onCloseCallback()
      }}
      title={edit ? `Edit Discount` : `Add Discount`}
      position="right"
    >
      {fetchingSingleDiscount ? (
        <Spinner fullScreen={false} />
      ) : (
        <form
          onSubmit={handleSubmit}
          className="h-full flex flex-col justify-between"
        >
          <div className="flex flex-col gap-4 flex-1">
            <div className="grid grid-cols-2 gap-4">
              <Dropdown
                menuClassName="!max-h-[200px]"
                menuButton={
                  <TextInput
                    type="text"
                    title="Discount type"
                    name="type"
                    placeholder="Select type"
                    value={values.type}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    hasIcon={false}
                    readOnly
                    extra={
                      <Icon
                        id={'left-caret'}
                        width={16}
                        height={16}
                        className="-rotate-90 text-sec-black"
                      />
                    }
                  />
                }
                menuItems={types.map((type) => {
                  return {
                    name: type,
                    value: type,
                    onClick: () => {
                      setFieldValue('type', type)
                    },
                  }
                })}
              />

              <Dropdown
                menuClassName="!max-h-[200px]"
                menuButton={
                  <TextInput
                    type="text"
                    title="Status"
                    name="status"
                    placeholder="Select status"
                    value={values.status}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    hasIcon={false}
                    readOnly
                    extra={
                      <Icon
                        id={'left-caret'}
                        width={16}
                        height={16}
                        className="-rotate-90 text-sec-black"
                      />
                    }
                  />
                }
                menuItems={statusType.map((status) => {
                  return {
                    name: status,
                    value: status,
                    onClick: () => {
                      setFieldValue('status', status)
                    },
                  }
                })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol('NGN')}
                customInput={TextInput}
                title="Minimum amount"
                type="text"
                name="minimumAmount"
                placeholder="Minimum amount"
                value={values.minimumAmount}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.minimumAmount && touched.minimumAmount
                    ? errors.minimumAmount
                    : ''
                }
                hasIcon={false}
                className="!border-divider-gray"
                decimalScale={2}
                thousandSeparator
              />

              <NumericFormat
                suffix={undefined}
                prefix={getCurrencySymbol('NGN')}
                decimalScale={2}
                thousandSeparator
                customInput={TextInput}
                title="Maximum amount"
                type="text"
                name="maximumAmount"
                placeholder="Maximum amount"
                value={values.maximumAmount}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.maximumAmount && touched.maximumAmount
                    ? errors.maximumAmount
                    : ''
                }
                hasIcon={false}
                className="!border-divider-gray"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <TextInput
                title="Value"
                type="text"
                name="value"
                placeholder="Input value"
                value={values.value}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={errors.value && touched.value ? errors.value : ''}
                hasIcon={false}
                className="!border-divider-gray"
              />

              <Dropdown
                menuClassName="!max-h-[200px]"
                menuButton={
                  <TextInput
                    type="text"
                    title="Value type"
                    name="type"
                    placeholder="Select value type"
                    value={values.valueType}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    hasIcon={false}
                    readOnly
                    extra={
                      <Icon
                        id={'left-caret'}
                        width={16}
                        height={16}
                        className="-rotate-90 text-sec-black"
                      />
                    }
                  />
                }
                menuItems={valueType.map((value) => {
                  return {
                    name: value,
                    value: value,
                    onClick: () => {
                      setFieldValue('valueType', value)
                    },
                  }
                })}
              />
            </div>

            <TextInput
              title="Code"
              type="text"
              name="code"
              placeholder="Input Code"
              value={values.code}
              onBlur={handleBlur}
              onChange={handleChange}
              errors={errors.code && touched.code ? errors.code : ''}
              hasIcon={false}
              className="!border-divider-gray"
            />

            <div className="grid grid-cols-2 gap-4">
              <TextInput
                title="Usage Limit"
                type="text"
                name="usageLimit"
                placeholder="Usage limit"
                value={values.usageLimit}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.usageLimit && touched.usageLimit
                    ? errors.usageLimit
                    : ''
                }
                hasIcon={false}
                className="!border-divider-gray"
              />

              <TextInput
                title="User usage limit"
                type="text"
                name="userUsageLimit"
                placeholder="Input user limit"
                value={values.userUsageLimit}
                onBlur={handleBlur}
                onChange={handleChange}
                errors={
                  errors.userUsageLimit && touched.userUsageLimit
                    ? errors.userUsageLimit
                    : ''
                }
                hasIcon={false}
                className="!border-divider-gray"
              />
            </div>

            <div className="flex items-center gap-3 justify-between">
              <p>Auto apply discount?</p>

              <CustSwitch
                checked={values.autoApply}
                disabled={false}
                onChange={() => {
                  setFieldValue('autoApply', !values.autoApply)
                }}
              />
            </div>

            {values.type === 'items' && (
              <div className="flex justify-end">
                <Button
                  label={'Select Items'}
                  type="button"
                  className="!w-fit"
                  onClick={() => {
                    handleModalOpen('selectProductModal')
                  }}
                  size="medium"
                  primary
                />
              </div>
            )}

            {values.type === 'items' && (
              <TableComponent
                sortable={false}
                headers={undefined}
                rows={
                  values.items.length
                    ? values.items.map((product, idx) => {
                        return {
                          id: `${product.name}-${product._id}`,
                          content: [
                            <div
                              key={`${idx}-name`}
                              className="w-full flex items-center justify-between gap-4"
                            >
                              <div className="max-w-fit text-left hover:underline flex items-center gap-4">
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="aspect-square w-[40px] rounded-md"
                                />
                                {product.name}
                              </div>

                              <button
                                className="max-w-fit"
                                type="button"
                                onClick={() => {
                                  const productsCopy = JSON.parse(
                                    JSON.stringify(values.items),
                                  )
                                  setFieldValue(
                                    'items',
                                    removeElementAtIndex(productsCopy, idx),
                                  )
                                }}
                              >
                                <Icon
                                  id="bin"
                                  width={16}
                                  height={16}
                                  className="text-danger"
                                />
                              </button>
                            </div>,
                          ],
                        }
                      })
                    : []
                }
                name="selected-products-table"
                loading={false}
                isEmpty={[undefined, 0].includes(values.items.length)}
                currentPage={1}
                totalDataCount={values.items.length}
                pageLimit={values.items.length}
                onPageChange={(page) => {}}
              />
            )}

            {modal === 'selectProductModal' && modalOpen && (
              <SelectProductModal
                open={modal === 'selectProductModal' && modalOpen}
                setOpen={setModalOpen}
                onCloseCallback={() => {
                  handleModalClose()
                }}
                selectedProducts={values.items}
                onSuccessCallback={(products: Product[]) => {
                  const productsCopy: Product[] = JSON.parse(
                    JSON.stringify(values.items),
                  )

                  setFieldValue('items', [
                    ...productsCopy,
                    ...products.filter(
                      (p) => !productsCopy.map((i) => i._id).includes(p._id),
                    ),
                  ])

                  handleModalClose()
                }}
              />
            )}
          </div>

          <Button
            label="Proceed"
            type="submit"
            className="!sticky bottom-0 mt-10 w-full"
            disabled={creating || editing}
            loading={creating || editing}
            primary
          />
        </form>
      )}
    </ModalComponent>
  )
}
